import { useCallback } from "react";
import { usePrinterStore } from "@/store/printerStore";
import type { PrinterSettings, SavedPort } from "@/store/printerStore";
import { isLaserOrPagePrinter, buildLaserTestPayload } from "@/utils/laserPrinterUtils";

type SerialPort = any;
type BluetoothDevice = any;
type BluetoothRemoteGATTCharacteristic = any;

const PRINTER_SERVICE = "000018f0-0000-1000-8000-00805f9b34fb";
const PRINTER_CHAR = "00002af1-0000-1000-8000-00805f9b34fb";

// ── port helpers ──────────────────────────────────────────────────────────────

export function portId(
  info: { usbVendorId?: number; usbProductId?: number },
  i: number
) {
  return info.usbVendorId
    ? `usb-${info.usbVendorId}-${info.usbProductId ?? 0}`
    : `serial-${i}`;
}

export function portLabel(
  info: { usbVendorId?: number; usbProductId?: number },
  i: number
) {
  if (info.usbVendorId) {
    const vid = info.usbVendorId.toString(16).toUpperCase().padStart(4, "0");
    const pid = (info.usbProductId ?? 0)
      .toString(16)
      .toUpperCase()
      .padStart(4, "0");
    return `USB ${vid}:${pid}`;
  }
  return `Serial port ${i + 1}`;
}

function friendlyError(e: unknown, fallback: string): string {
  const err = e as { name?: string; message?: string };
  if (!err) return fallback;
  if (err.name === "NotFoundError") return "Device selection cancelled.";
  if (err.name === "NotAllowedError") return "Permission denied. Allow access when the browser prompts.";
  if (err.name === "SecurityError") {
    return "Blocked by the browser. Use Chrome or Edge over HTTPS (or localhost).";
  }
  if (err.name === "NetworkError") return "Device disconnected. Power on the printer and try again.";
  if (err.name === "InvalidStateError") return "Port busy. Disconnect and try again.";
  return err.message || fallback;
}

async function ensurePortClosed(port: SerialPort) {
  try {
    if (port.readable || port.writable) {
      await port.close();
    }
  } catch {
    /* already closed */
  }
}

// ── USB ───────────────────────────────────────────────────────────────────────

async function connectUsb(
  settings: PrinterSettings
): Promise<{
  port: SerialPort;
  writer: WritableStreamDefaultWriter<Uint8Array>;
}> {
  const serial = (navigator as any).serial;
  if (!serial) {
    throw new Error("USB printing needs Chrome or Edge with Web Serial support.");
  }

  const all: SerialPort[] = await serial.getPorts();
  let port: SerialPort | undefined;

  if (settings.defaultPortId) {
    port = all.find((p, i) => portId(p.getInfo(), i) === settings.defaultPortId);
    if (!port) {
      throw new Error(
        "Saved USB printer not found. Click “+ Add printer” and grant access again."
      );
    }
  } else if (all.length === 1) {
    port = all[0];
  } else if (all.length === 0) {
    throw new Error("No USB printer granted yet. Click “+ Add printer” first.");
  } else {
    throw new Error("Multiple USB printers found. Set a default in the list above.");
  }

  await ensurePortClosed(port);

  try {
    await port.open({
      baudRate: settings.baudRate,
      dataBits: settings.dataBits,
      stopBits: settings.stopBits,
      parity: settings.parity,
    });
  } catch (e) {
    throw new Error(friendlyError(e, "Could not open USB port. Unplug/replug the printer and retry."));
  }

  if (!port.writable) {
    throw new Error("USB port opened but is not writable.");
  }

  return { port, writer: port.writable.getWriter() };
}

// ── Bluetooth ─────────────────────────────────────────────────────────────────

/** Session-only handle — getDevices() is often empty after reload until user re-pairs. */
let rememberedBtDevice: BluetoothDevice | null = null;

/** Bumped on user-initiated connect so auto-connect cannot overwrite the result. */
let connectGeneration = 0;

function persistBluetoothDevice(device: BluetoothDevice) {
  rememberedBtDevice = device;
  const id = device.id ?? null;
  const name = device.name ?? device.id ?? null;
  usePrinterStore.setState((s) => ({
    draft: {
      ...s.draft,
      connectionType: "bluetooth",
      bluetoothDeviceId: id,
      bluetoothDeviceName: name,
    },
    settings: {
      ...s.settings,
      connectionType: "bluetooth",
      bluetoothDeviceId: id,
      bluetoothDeviceName: name,
    },
  }));
}

function matchRememberedDevice(settings: PrinterSettings): BluetoothDevice | undefined {
  if (!rememberedBtDevice) return undefined;
  const id = settings.bluetoothDeviceId;
  const name = settings.bluetoothDeviceName;
  if (id && rememberedBtDevice.id === id) return rememberedBtDevice;
  if (name && rememberedBtDevice.name === name) return rememberedBtDevice;
  return undefined;
}

async function resolveBluetoothDevice(
  settings: PrinterSettings,
  allowPicker: boolean
): Promise<BluetoothDevice> {
  const bt = (navigator as any).bluetooth;
  if (!bt) {
    throw new Error("Bluetooth printing needs Chrome or Edge with Web Bluetooth support.");
  }

  const remembered = matchRememberedDevice(settings);
  if (remembered) return remembered;

  // User-gesture path: open picker immediately when we have no session handle.
  // Awaiting getDevices() first often burns transient user activation, so
  // requestDevice would then fail after Complete Sale.
  if (allowPicker && !rememberedBtDevice) {
    const picked: BluetoothDevice = await bt.requestDevice({
      acceptAllDevices: true,
      optionalServices: [PRINTER_SERVICE],
    });
    rememberedBtDevice = picked;
    return picked;
  }

  if (typeof bt.getDevices === "function") {
    try {
      const paired: BluetoothDevice[] = await bt.getDevices();
      let device: BluetoothDevice | undefined;
      if (settings.bluetoothDeviceId) {
        device = paired.find((d) => d.id === settings.bluetoothDeviceId);
      }
      if (!device && settings.bluetoothDeviceName) {
        device = paired.find((d) => d.name === settings.bluetoothDeviceName);
      }
      // Single granted device + we have a saved printer → use it
      if (!device && paired.length === 1 && (settings.bluetoothDeviceId || settings.bluetoothDeviceName)) {
        device = paired[0];
      }
      if (device) {
        rememberedBtDevice = device;
        return device;
      }
    } catch {
      /* getDevices can throw; fall through to picker */
    }
  }

  if (!allowPicker) {
    throw new Error(
      "Bluetooth printer not connected. Use Test connection or Re-pair (browser may require picking the device again)."
    );
  }

  const device: BluetoothDevice = await bt.requestDevice({
    acceptAllDevices: true,
    optionalServices: [PRINTER_SERVICE],
  });
  rememberedBtDevice = device;
  return device;
}

async function connectBluetooth(
  settings: PrinterSettings,
  opts?: { allowPicker?: boolean }
): Promise<{
  device: BluetoothDevice;
  characteristic: BluetoothRemoteGATTCharacteristic;
}> {
  const allowPicker = opts?.allowPicker !== false;
  let device: BluetoothDevice;

  try {
    device = await resolveBluetoothDevice(settings, allowPicker);
  } catch (e) {
    throw new Error(friendlyError(e, "Bluetooth pairing failed."));
  }

  if (!device.gatt) {
    throw new Error("This Bluetooth device does not support GATT.");
  }

  let server;
  try {
    server = device.gatt.connected
      ? device.gatt
      : await device.gatt.connect();
  } catch (e) {
    // Stale getDevices handle — clear cache and open picker when allowed
    rememberedBtDevice = null;
    if (!allowPicker) {
      throw new Error(
        friendlyError(e, "Could not connect over Bluetooth. Keep the printer nearby and powered on.")
      );
    }
    try {
      const bt = (navigator as any).bluetooth;
      device = await bt.requestDevice({
        acceptAllDevices: true,
        optionalServices: [PRINTER_SERVICE],
      });
      rememberedBtDevice = device;
      if (!device.gatt) {
        throw new Error("This Bluetooth device does not support GATT.");
      }
      server = device.gatt.connected
        ? device.gatt
        : await device.gatt.connect();
    } catch (e2) {
      throw new Error(
        friendlyError(e2, "Could not connect over Bluetooth. Keep the printer nearby and powered on.")
      );
    }
  }

  let characteristic: BluetoothRemoteGATTCharacteristic;
  try {
    const svc = await server.getPrimaryService(PRINTER_SERVICE);
    characteristic = await svc.getCharacteristic(PRINTER_CHAR);
  } catch {
    const services = await server.getPrimaryServices();
    if (!services.length) {
      throw new Error("No Bluetooth print services found on this device.");
    }
    const chars = await services[0].getCharacteristics();
    const writable = chars.find(
      (c: BluetoothRemoteGATTCharacteristic) =>
        c.properties.write || c.properties.writeWithoutResponse
    );
    if (!writable) {
      throw new Error("No writable Bluetooth characteristic found on this device.");
    }
    characteristic = writable;
  }

  persistBluetoothDevice(device);

  return { device, characteristic };
}

const encoder = new TextEncoder();

function buildTestPrintPayload(settings: PrinterSettings): Uint8Array {
  if (isLaserOrPagePrinter(settings.windowsPrinterName, settings.paperWidth)) {
    return buildLaserTestPayload(settings.headerText, settings.windowsPrinterName || "");
  }
  const header = settings.headerText?.trim() || "TEST PRINT";
  const lines = [
    header,
    "------------------------------",
    "PRINTER TEST PAGE",
    `Time: ${new Date().toLocaleString()}`,
    "",
    "This is a sample receipt printout.",
    "Please verify that the printer feeds paper correctly.",
    "",
  ];

  const payload = encoder.encode(
    lines.join("\n") + "\n".repeat(Math.max(1, settings.feedLines))
  );
  if (!settings.autoCut) return payload;

  const cut = new Uint8Array([0x1d, 0x56, 0x42, 0x00]);
  const result = new Uint8Array(payload.length + cut.length);
  result.set(payload, 0);
  result.set(cut, payload.length);
  return result;
}

// ── singleton connection state ────────────────────────────────────────────────

interface LiveConnection {
  type: "usb" | "bluetooth" | "wifi" | null;
  usbPort: SerialPort | null;
  usbWriter: WritableStreamDefaultWriter<Uint8Array> | null;
  ws: WebSocket | null;
  btDevice: BluetoothDevice | null;
  btChar: BluetoothRemoteGATTCharacteristic | null;
}

const live: LiveConnection = {
  type: null,
  usbPort: null,
  usbWriter: null,
  ws: null,
  btDevice: null,
  btChar: null,
};

let printingBusy = false;

async function tearDownLive() {
  try {
    if (live.type === "usb") {
      try {
        live.usbWriter?.releaseLock();
      } catch {
        /* ignore */
      }
      if (live.usbPort) await ensurePortClosed(live.usbPort);
    } else if (live.type === "wifi") {
      live.ws?.close();
    } else if (live.type === "bluetooth") {
      try {
        live.btDevice?.gatt?.disconnect();
      } catch {
        /* ignore */
      }
    }
  } finally {
    live.type = null;
    live.usbPort = null;
    live.usbWriter = null;
    live.ws = null;
    live.btDevice = null;
    live.btChar = null;
  }
}

/** True when a thermal path (Print Helper, BT, or USB serial) is configured. */
export function isThermalPrinterConfigured(settings?: PrinterSettings): boolean {
  const s = settings || usePrinterStore.getState().settings;
  if (s.connectionType === "bluetooth") {
    return !!(s.bluetoothDeviceId || s.bluetoothDeviceName);
  }
  if (s.usePrintHelper !== false && s.windowsPrinterName) return true;
  return !!s.defaultPortId;
}

async function ensureLiveConnection(
  settings: PrinterSettings,
  opts?: { allowPicker?: boolean; generation?: number }
): Promise<void> {
  const wantBt = settings.connectionType === "bluetooth";
  const isUsbReady = !wantBt && live.type === "usb" && !!live.usbWriter;
  const isBluetoothReady = wantBt && live.type === "bluetooth" && !!live.btChar;
  if (isUsbReady || isBluetoothReady) return;

  const stillCurrent = () =>
    opts?.generation == null || opts.generation === connectGeneration;

  if (!stillCurrent()) return;
  // Skip await when already idle — preserves user-gesture for BT requestDevice
  if (live.type !== null) {
    await tearDownLive();
    if (!stillCurrent()) return;
  }

  const { setStatus, setError } = usePrinterStore.getState();
  setStatus("connecting");
  setError(null);
  try {
    if (wantBt) {
      const { device, characteristic } = await connectBluetooth(settings, opts);
      if (!stillCurrent()) return;
      live.type = "bluetooth";
      live.btDevice = device;
      live.btChar = characteristic;
    } else {
      const { port, writer } = await connectUsb(settings);
      if (!stillCurrent()) return;
      live.type = "usb";
      live.usbPort = port;
      live.usbWriter = writer;
    }
    if (!stillCurrent()) return;
    setStatus("connected");
  } catch (e: any) {
    if (!stillCurrent()) throw e;
    await tearDownLive();
    setStatus("error");
    setError(e?.message ?? "Connection failed.");
    throw e;
  }
}

async function writeLiveBytes(bytes: Uint8Array): Promise<void> {
  if (printingBusy) {
    throw new Error("Printer is busy. Wait for the current job to finish.");
  }
  printingBusy = true;
  try {
    if (live.type === "usb" && live.usbWriter) {
      await live.usbWriter.write(bytes);
      return;
    }
    if (live.type === "bluetooth" && live.btChar) {
      const char = live.btChar;
      const CHUNK_SIZE = 120;
      const DELAY = 50;
      const canWO = char.properties.writeWithoutResponse;
      const canW = char.properties.write;
      if (!canWO && !canW) {
        throw new Error("Bluetooth characteristic is not writable.");
      }
      for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
        const chunk = bytes.slice(i, i + CHUNK_SIZE);
        try {
          if (canWO) {
            await char.writeValueWithoutResponse(chunk);
          } else {
            await char.writeValue(chunk);
          }
        } catch {
          await new Promise((r) => setTimeout(r, 120));
          await char.writeValue(chunk);
        }
        await new Promise((r) => setTimeout(r, DELAY));
      }
      return;
    }
    throw new Error("Not connected. Use Test connection first.");
  } finally {
    printingBusy = false;
  }
}

/**
 * Send ESC/POS bytes via Print Helper (USB) or live BT/USB serial connection.
 * @param allowPicker — when false, never open BT device picker (silent / auto-print).
 */
export async function printEscPosBytes(
  bytes: Uint8Array,
  opts?: { allowPicker?: boolean; settings?: PrinterSettings }
): Promise<void> {
  const settings = opts?.settings || usePrinterStore.getState().settings;
  const allowPicker = opts?.allowPicker !== false;

  const useHelper =
    settings.connectionType !== "bluetooth" &&
    settings.usePrintHelper !== false &&
    !!settings.windowsPrinterName;

  if (useHelper && settings.windowsPrinterName) {
    const { printViaHelper } = await import("@/services/printHelper");
    await printViaHelper(settings.windowsPrinterName, bytes);
    return;
  }

  await ensureLiveConnection(settings, { allowPicker });
  await writeLiveBytes(bytes);
}

/** Best-effort reconnect when autoConnect is enabled (no BT picker). */
export async function tryAutoConnectPrinter(): Promise<void> {
  const settings = usePrinterStore.getState().settings;
  if (!settings.autoConnect) return;
  if (!isThermalPrinterConfigured(settings)) return;

  // Snapshot only — do not bump, so a user Test connection can supersede us
  const gen = connectGeneration;
  try {
    const useHelper =
      settings.connectionType !== "bluetooth" &&
      settings.usePrintHelper !== false &&
      !!settings.windowsPrinterName;
    if (useHelper) {
      const { pingPrintHelper, isHelperVersionOk } = await import("@/services/printHelper");
      const ping = await pingPrintHelper();
      if (gen !== connectGeneration) return;
      if (ping && isHelperVersionOk(ping.version)) {
        usePrinterStore.getState().setStatus("connected");
        usePrinterStore.getState().setError(null);
      }
      return;
    }
    await ensureLiveConnection(settings, { allowPicker: false, generation: gen });
  } catch {
    // Quiet — do not leave a red error from background reconnect
    if (gen === connectGeneration) {
      usePrinterStore.getState().setStatus("idle");
      usePrinterStore.getState().setError(null);
    }
  }
}

/**
 * Start (or reuse) thermal connection under a user gesture — call from Complete Sale
 * before any await so Bluetooth requestDevice still has transient activation.
 */
export function primePrinterForAutoPrint(): Promise<void> {
  const settings = usePrinterStore.getState().settings;
  if (!settings.autoPrintOnSale) return Promise.resolve();
  if (!isThermalPrinterConfigured(settings)) return Promise.resolve();

  const useHelper =
    settings.connectionType !== "bluetooth" &&
    settings.usePrintHelper !== false &&
    !!settings.windowsPrinterName;
  if (useHelper) return Promise.resolve();

  // Bluetooth / serial: allow picker — Complete Sale is a user gesture
  return ensureLiveConnection(settings, { allowPicker: true }).catch((e) => {
    // Surface later via silent print; don't throw into the sale click path
    console.warn("Printer prime failed", e);
  });
}

// ── hook ──────────────────────────────────────────────────────────────────────

export function usePrinterConnection() {
  const { draft, ports, status, setStatus, setError, setPorts, addPort } =
    usePrinterStore();

  const refreshPorts = useCallback(async () => {
    if (!("serial" in navigator)) return;
    try {
      const raw: SerialPort[] = await (navigator as any).serial.getPorts();
      setPorts(
        raw.map((p, i) => {
          const info = p.getInfo();
          return { id: portId(info, i), label: portLabel(info, i), ...info };
        })
      );
    } catch {
      /* Serial not available */
    }
  }, [setPorts]);

  const requestNewPort = useCallback(async (): Promise<SavedPort | null> => {
    if (!("serial" in navigator)) {
      setError("USB printing needs Chrome or Edge with Web Serial support.");
      return null;
    }
    try {
      const port: SerialPort = await (navigator as any).serial.requestPort();
      const info = port.getInfo();
      const all: SerialPort[] = await (navigator as any).serial.getPorts();
      const i = all.findIndex((p) => {
        const pi = p.getInfo();
        return (
          pi.usbVendorId === info.usbVendorId &&
          pi.usbProductId === info.usbProductId
        );
      });
      const sp: SavedPort = {
        id: portId(info, i >= 0 ? i : all.length),
        label: portLabel(info, i >= 0 ? i : all.length),
        usbVendorId: info.usbVendorId,
        usbProductId: info.usbProductId,
      };
      addPort(sp);
      setError(null);
      return sp;
    } catch (e) {
      setError(friendlyError(e, "Could not add USB printer."));
      return null;
    }
  }, [addPort, setError]);

  const scanBluetooth = useCallback(async (): Promise<boolean> => {
    const bt = (navigator as any).bluetooth;
    if (!bt) {
      setError("Bluetooth printing needs Chrome or Edge with Web Bluetooth support.");
      return false;
    }
    try {
      const device: BluetoothDevice = await bt.requestDevice({
        acceptAllDevices: true,
        optionalServices: [PRINTER_SERVICE],
      });
      persistBluetoothDevice(device);
      setError(null);
      return true;
    } catch (e) {
      setError(friendlyError(e, "Bluetooth pairing cancelled or failed."));
      return false;
    }
  }, [setError]);

  const disconnect = useCallback(async () => {
    connectGeneration += 1;
    await tearDownLive();
    setStatus("idle");
    setError(null);
  }, [setStatus, setError]);

  const connect = useCallback(async (): Promise<boolean> => {
    const gen = ++connectGeneration;
    setStatus("connecting");
    setError(null);

    const type = draft.connectionType === "bluetooth" ? "bluetooth" : "usb";

    try {
      await tearDownLive();
      if (gen !== connectGeneration) return false;

      if (type === "usb") {
        const { port, writer } = await connectUsb(draft);
        live.type = "usb";
        live.usbPort = port;
        live.usbWriter = writer;
      } else {
        // User gesture: always allow the Bluetooth picker if reconnect fails
        const { device, characteristic } = await connectBluetooth(draft, {
          allowPicker: true,
        });
        live.type = "bluetooth";
        live.btDevice = device;
        live.btChar = characteristic;
      }

      if (gen !== connectGeneration) {
        await tearDownLive();
        return false;
      }

      setStatus("connected");
      return true;
    } catch (e: any) {
      if (gen !== connectGeneration) return false;
      await tearDownLive();
      setStatus("error");
      setError(e?.message ?? "Connection failed.");
      return false;
    }
  }, [draft, setStatus, setError]);

  const sendBytes = useCallback(async (bytes: Uint8Array): Promise<boolean> => {
    try {
      if (printingBusy) {
        throw new Error("Printer is busy. Wait for the current job to finish.");
      }
      printingBusy = true;

      if (live.type === "usb" && live.usbWriter) {
        await live.usbWriter.write(bytes);
        return true;
      }

      if (live.type === "bluetooth" && live.btChar) {
        const char = live.btChar;
        const CHUNK_SIZE = 120;
        const DELAY = 50;
        const canWO = char.properties.writeWithoutResponse;
        const canW = char.properties.write;

        if (!canWO && !canW) {
          throw new Error("Bluetooth characteristic is not writable.");
        }

        for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
          const chunk = bytes.slice(i, i + CHUNK_SIZE);
          try {
            if (canWO) {
              await char.writeValueWithoutResponse(chunk);
            } else {
              await char.writeValue(chunk);
            }
          } catch {
            await new Promise((r) => setTimeout(r, 120));
            await char.writeValue(chunk);
          }
          await new Promise((r) => setTimeout(r, DELAY));
        }
        return true;
      }

      throw new Error("Not connected. Use Test connection first.");
    } catch (e: any) {
      setError(friendlyError(e, "Print failed."));
      return false;
    } finally {
      printingBusy = false;
    }
  }, [setError]);

  const ensureConnection = useCallback(async (): Promise<boolean> => {
    const wantBt = draft.connectionType === "bluetooth";
    const isUsbReady = !wantBt && live.type === "usb" && !!live.usbWriter;
    const isBluetoothReady =
      wantBt && live.type === "bluetooth" && !!live.btChar;

    if (isUsbReady || isBluetoothReady) return true;
    return connect();
  }, [draft.connectionType, connect]);

  const testPrint = useCallback(async (): Promise<boolean> => {
    setStatus("connecting");
    setError(null);

    try {
      const connected = await ensureConnection();
      if (!connected) return false;

      const payload = buildTestPrintPayload(draft);
      const sent = await sendBytes(payload);
      if (!sent) {
        setStatus("error");
        return false;
      }

      setStatus("connected");
      return true;
    } catch (e: any) {
      setStatus("error");
      setError(e?.message ?? "Test print failed.");
      return false;
    }
  }, [ensureConnection, sendBytes, setStatus, setError, draft]);

  return {
    connect,
    disconnect,
    sendBytes,
    testPrint,
    refreshPorts,
    requestNewPort,
    scanBluetooth,
    status,
    ports,
  };
}
